import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateMarketSetting } from "@/services/Merchant.service";
import { toast } from "react-toastify";
import { useEffect } from "react";
import type { MarketRateProposal } from "../columns";

const editRateSchema = z.object({
  currencyPair: z.string().min(1, "Select a currency pair"),
  rateSource: z.string().min(1, "Rate source is required"),
  buyingPriceMarkup: z.string().min(1, "Buying price markup is required"),
  sellingPriceMarkup: z.string().min(1, "Selling price markup is required"),
});

type EditRateFormValues = z.infer<typeof editRateSchema>;

const rateSourceOptions = [
  { value: "MANUAL", label: "Manual" },
  { value: "FLAT", label: "Flat" },
  { value: "PERCENTAGE", label: "Percentage" },
];

type EditRateProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rate: MarketRateProposal | null;
  onSuccess?: () => void;
};

export default function EditRate({
  open,
  onOpenChange,
  rate,
  onSuccess,
}: EditRateProps) {
  const form = useForm<EditRateFormValues>({
    resolver: zodResolver(editRateSchema),
    defaultValues: {
      currencyPair: "",
      rateSource: "",
      buyingPriceMarkup: "",
      sellingPriceMarkup: "",
    },
  });

  useEffect(() => {
    if (rate && open) {
      form.reset({
        currencyPair: rate.currency,
        rateSource: rate.type,
        buyingPriceMarkup: String(rate.buyingOffset),
        sellingPriceMarkup: String(rate.sellingOffset),
      });
    }
  }, [rate, open, form]);

  const onSubmit = async (data: EditRateFormValues) => {
    if (!rate) return;
    try {
      console.log(data, "data", rate.id);
      await updateMarketSetting(rate.id, {
        supportedPairId: rate.supportedPairId,
        offset: data.rateSource,
        buying: Number(data.buyingPriceMarkup),
        selling: Number(data.sellingPriceMarkup),
      });
      toast.success("Market rate updated successfully");
      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to update market rate"
      );
    }
  };

  return (
    <CustomModal open={open} onOpenChange={onOpenChange} size="2xl">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader className="text-center py-4 md:py-6 px-4 md:px-8 border-b bg-background rounded-t-[16px]">
            <DialogTitle className="text-lg font-semibold text-primary-3">
              Edit rate
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 px-4 md:px-8 py-4 md:py-6 max-h-[70vh] overflow-y-auto">
            <FormField
              control={form.control}
              name="currencyPair"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-primary-3">
                    Currency pair
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="h-10 rounded-[8px] border-grey-300 bg-muted/50"
                      {...field}
                      disabled
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rateSource"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-primary-3">
                    <span className="text-error">*</span> Rate Source
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-10 rounded-[8px] border-grey-300">
                        <SelectValue placeholder="select rate source" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {rateSourceOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="buyingPriceMarkup"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-primary-3">
                    <span className="text-error">*</span> Buying price markup
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="h-10 rounded-[8px] border-grey-300"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sellingPriceMarkup"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-primary-3">
                    <span className="text-error">*</span> Selling price markup
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="h-10 rounded-[8px] border-grey-300"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <DialogFooter className="px-4 md:px-8 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px] border-t">
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                className="text-primary0 h-12 rounded-[10px] px-4 hover:text-primary0"
              >
                <ChevronLeft className="mr-2" />
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] px-4 text-white"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Save changes
                  <ChevronRight className="ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </CustomModal>
  );
}
